/**
 * Reservoir Templates List component
 */
import { copyLayerTemplate, deleteLayerTemplate, getLayerTemplate, getLayerTemplates } from '@/api/layerTemplates';
import { isHiddenLayerAttribute } from '@/data/hiddenLayerAttributes';
import { LayerTemplate } from '@/types/layerTemplate';
import { Add as AddIcon, Search as SearchIcon } from '@mui/icons-material';
import {
    Alert,
    AlertTitle,
    Box,
    Button,
    CircularProgress,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    Menu,
    Select,
    TextField,
    Typography
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import ConfirmDeleteDialog from '../common/ConfirmDeleteDialog';
import LayerTemplateImportCsvModal from './LayerTemplateImportCsvModal';
import LayerTemplateCard from './LayerTemplateCard';
import { LayerTemplateCreateModal } from './LayerTemplateCreateModal';
import { LayerTemplateDetailModal } from './LayerTemplateDetailModal';

const LayersTemplatesList: React.FC = () => {
  const [templates, setTemplates] = useState<LayerTemplate[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchField, setSearchField] = useState<'name' | 'description'>('name');
  const [publicFilter, setPublicFilter] = useState<'all' | 'public' | 'private'>('all');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [selectedTemplate, setSelectedTemplate] = useState<LayerTemplate | null>(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [createMenuAnchorEl, setCreateMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState<LayerTemplate | null>(null);
  const isCreateMenuOpen = Boolean(createMenuAnchorEl);

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('Fetching reservoir templates...');
      const response = await getLayerTemplates();
      console.log('Reservoir templates response:', response);
      setTemplates(response.templates);
      console.log('Reservoir templates set:', response.templates);
    } catch (err) {
      console.error('Error fetching reservoir templates:', err);
      setError('Failed to load reservoir templates');
    } finally {
      setLoading(false);
    }
  };

  const filteredTemplates = templates.filter(template => {
    const normalizedSearch = searchTerm.toLowerCase().trim();
    const matchesSearch = !normalizedSearch || (
      searchField === 'name'
        ? template.name.toLowerCase().includes(normalizedSearch)
        : (template.description || '').toLowerCase().includes(normalizedSearch)
    );
    const matchesPublic = publicFilter === 'all' ||
      (publicFilter === 'public' && template.is_public) ||
      (publicFilter === 'private' && !template.is_public);
    const matchesType = typeFilter === 'all' || template.type === typeFilter;

    return matchesSearch && matchesPublic && matchesType;
  });

  const handleTemplateClick = (template: LayerTemplate) => {
    setSelectedTemplate(template);
    setIsDetailModalOpen(true);
  };

  const handleTemplateEdit = (template: LayerTemplate) => {
    setSelectedTemplate(template);
    setIsDetailModalOpen(true);
  };

  const handleTemplateCopy = async (template: LayerTemplate) => {
    try {
      setLoading(true);
      await copyLayerTemplate(template.id);
      await fetchTemplates();
    } catch (err) {
      console.error('Error copying template:', err);
      setError('Failed to copy template');
    } finally {
      setLoading(false);
    }
  };

  const handleTemplateDelete = async (template: LayerTemplate) => {
    setTemplateToDelete(template);
    setDeleteDialogOpen(true);
  };

  const escapeCsvCell = (value: string): string => {
    if (value.includes('"') || value.includes(',') || value.includes('\n')) {
      return `"${value.replace(/"/g, '""')}"`;
    }
    return value;
  };

  const downloadCsv = (fileName: string, content: string) => {
    const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleTemplateExport = async (template: LayerTemplate) => {
    try {
      const templateDetail = await getLayerTemplate(template.id);
      const lines = ['attribute,value'];
      templateDetail.properties.forEach((property) => {
        if (isHiddenLayerAttribute(null, property.name)) {
          return;
        }
        const attribute = property.name ?? '';
        const rawValue =
          property.value === null || property.value === undefined ? '' : String(property.value);
        lines.push(`${escapeCsvCell(attribute)},${escapeCsvCell(rawValue)}`);
      });

      const safeName = (templateDetail.name || template.name || 'template')
        .replace(/[\\/:*?"<>|]/g, '_')
        .replace(/\s+/g, '_');
      downloadCsv(`${safeName}.csv`, lines.join('\n'));
    } catch (err) {
      console.error('Error exporting template CSV:', err);
      setError('Failed to export template CSV');
    }
  };

  const handleOpenCreateMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    setCreateMenuAnchorEl(event.currentTarget);
  };

  const handleCloseCreateMenu = () => {
    setCreateMenuAnchorEl(null);
  };

  const handleConfirmDelete = async () => {
    if (!templateToDelete) return;

    try {
      setLoading(true);
      await deleteLayerTemplate(templateToDelete.id);
      await fetchTemplates();
      setDeleteDialogOpen(false);
      setTemplateToDelete(null);
    } catch (err) {
      console.error('Error deleting template:', err);
      setError('Failed to delete template');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 8 }}>
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ mt: 2 }}>Loading layer templates...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert
        severity="error"
        sx={{ mb: 3 }}
        action={
          <Typography
            component="button"
            onClick={fetchTemplates}
            sx={{
              background: 'none',
              border: 'none',
              color: 'inherit',
              textDecoration: 'underline',
              cursor: 'pointer'
            }}
          >
            Try again
          </Typography>
        }
      >
        <AlertTitle>Error</AlertTitle>
        {error}
      </Alert>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', mb: 3 }}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleOpenCreateMenu}
        >
          Create Template
        </Button>
        <Menu
          anchorEl={createMenuAnchorEl}
          open={isCreateMenuOpen}
          onClose={handleCloseCreateMenu}
        >
          <MenuItem
            onClick={() => {
              setIsCreateModalOpen(true);
              handleCloseCreateMenu();
            }}
          >
            Manual
          </MenuItem>
          <MenuItem
            onClick={() => {
              setIsImportModalOpen(true);
              handleCloseCreateMenu();
            }}
          >
            Import file (csv)
          </MenuItem>
        </Menu>
      </Box>

      {/* Filters */}
      <Box sx={{ mb: 4, display: 'flex', gap: 2, alignItems: 'center' }}>
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Search in</InputLabel>
          <Select
            value={searchField}
            onChange={(e) => setSearchField(e.target.value as 'name' | 'description')}
            label="Search in"
          >
            <MenuItem value="name">Name</MenuItem>
            <MenuItem value="description">Description</MenuItem>
          </Select>
        </FormControl>

        <TextField
          placeholder={searchField === 'name' ? 'Search by name...' : 'Search by description...'}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
          }}
          sx={{ minWidth: 300 }}
          size="small"
        />

        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Filter by visibility</InputLabel>
          <Select
            value={publicFilter}
            onChange={(e) => setPublicFilter(e.target.value as 'all' | 'public' | 'private')}
            label="Filter by visibility"
          >
            <MenuItem value="all">All templates</MenuItem>
            <MenuItem value="public">Public only</MenuItem>
            <MenuItem value="private">Private only</MenuItem>
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Filter by type</InputLabel>
          <Select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            label="Filter by type"
          >
            <MenuItem value="all">All types</MenuItem>
            <MenuItem value="oil">Oil</MenuItem>
            <MenuItem value="gas">Gas</MenuItem>
            <MenuItem value="oil_and_gas">Oil & Gas</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Templates Grid */}
      {filteredTemplates.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            {searchTerm || publicFilter !== 'all' || typeFilter !== 'all'
              ? "No reservoir templates match your filters"
              : "No reservoir templates found"
            }
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {searchTerm || publicFilter !== 'all' || typeFilter !== 'all'
              ? "Try adjusting your search criteria or filters"
              : "Create your first reservoir template to get started"
            }
          </Typography>
        </Box>
      ) : (
        <Grid container spacing={3}>
          {filteredTemplates.map((template) => (
            <Grid item key={template.id} xs={12} sm={6} md={4} lg={3}>
              <LayerTemplateCard
                template={template}
                onClick={handleTemplateClick}
                onEdit={handleTemplateEdit}
                onCopy={handleTemplateCopy}
                onDelete={handleTemplateDelete}
                onExport={handleTemplateExport}
              />
            </Grid>
          ))}
        </Grid>
      )}

      {/* Results count */}
      <Box sx={{ mt: 3, textAlign: 'right' }}>
        <Typography variant="body2" color="text.secondary">
          Showing {filteredTemplates.length} of {templates.length} reservoir templates
        </Typography>
      </Box>

      {/* Template Detail Modal */}
      <LayerTemplateDetailModal
        open={isDetailModalOpen}
        templateId={selectedTemplate?.id || null}
        onClose={() => {
          setIsDetailModalOpen(false);
          setSelectedTemplate(null);
        }}
        onTemplateUpdated={() => {
          fetchTemplates();
          setIsDetailModalOpen(false);
          setSelectedTemplate(null);
        }}
      />

      {/* Create Template Modal */}
      <LayerTemplateCreateModal
        open={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onTemplateCreated={() => {
          fetchTemplates();
          setIsCreateModalOpen(false);
        }}
      />

      <LayerTemplateImportCsvModal
        open={isImportModalOpen}
        onClose={() => setIsImportModalOpen(false)}
        onTemplateCreated={() => {
          fetchTemplates();
          setIsImportModalOpen(false);
        }}
      />

      {/* Confirm Delete Dialog */}
      <ConfirmDeleteDialog
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setTemplateToDelete(null);
        }}
        onConfirm={handleConfirmDelete}
        title="Delete Reservoir Template"
        itemName={templateToDelete?.name}
        itemType="reservoir template"
        loading={loading}
      />
    </Box>
  );
};

export default LayersTemplatesList;
